import { db } from '@/core/db/dexie'
import { sessionStore } from '@/core/auth/SessionStore'
import type { OutCarreteiro, OutViagem } from '@/core/db/entities'
import {
  ctxDaViagem,
  errosDaFicha,
  migrarEstado,
  paraRegra,
  viagemProducao,
  type CarreteiroFichaState,
  type ViagemDraft,
} from './carreteiroStore'
import { normalizarViagem } from './carreteiroRegras'
import { upsertDraft } from './draftCarreteiroRepo'
import { pushCarreteiroOutbox, type PushSummary } from '@/core/sync/carreteiroSync'
import { triggerForegroundSync } from '@/core/sync/foregroundSync'
import { requestBgSync } from '@/core/sync/registerBgSync'

/**
 * Persiste a ficha + viagens no outbox (2 tabelas) e dispara push imediato.
 *
 * Antes de gravar: valida todas as viagens com as regras do SIGA e normaliza
 * cada uma (concreto = ficha, qde_vg = 1, NF/largura/comprimento só com NF,
 * posição só onde é permitida). O estado completo vai junto em `draftState`
 * para a ficha poder ser reaberta se o servidor recusar.
 */
export async function saveCarreteiroFicha(
  state: CarreteiroFichaState,
): Promise<{ pushResult: PushSummary | null }> {
  const user = sessionStore.current()
  if (!user) throw new Error('Sem sessão de usuário')

  if (state.obraId == null || state.mveId == null) {
    throw new Error('Ficha incompleta — preencha obra e caminhão')
  }
  if (state.viagens.length === 0) {
    throw new Error('Ficha sem viagens')
  }
  const erro = errosDaFicha(state)[0]
  if (erro) {
    throw new Error(`Viagem ${erro.indice + 1}: ${erro.erros[0]?.mensagem ?? 'dados inválidos'}`)
  }

  const ficha: OutCarreteiro = {
    id: state.id,
    obraId: state.obraId,
    obraNome: state.obraNome,
    projetoId: state.projetoId,
    tipo: state.tipo,
    mveId: state.mveId,
    mvePrefixo: state.mvePrefixo,
    chefeEquipeId: state.chefeEquipeId,
    chefeEquipeNome: state.chefeEquipeNome,
    cadastradorId: user.userId,
    dt: state.dt,
    proprio: state.mveProprio === true,
    // O servidor numera a ficha (terceiro e frota própria). Rascunho antigo de próprio ainda pode trazer o nº digitado.
    ficha: state.mveProprio ? (state.fichaNumero ?? '').trim() || null : null,
    aguardandoParteDiaria: false,
    aguardandoMotivo: null,
    kmInicial: state.mveProprio ? null : state.kmInicial,
    kmFinal: state.mveProprio ? null : state.kmFinal,
    chapaEncarregado: state.chapaEncarregado,
    fichaConcreto: state.fichaConcreto,
    capturedAt: new Date().toISOString(),
    cadastradorNome: user.userNome,
    syncStatus: 'PENDING',
    failureReason: null,
    rejeicao: null,
    draftState: JSON.stringify(migrarEstado({ ...state, rejeicao: null })),
  }

  const viagens: OutViagem[] = state.viagens.map((v) => draftToViagem(v, state))

  await db.transaction('rw', db.out_carreteiro, db.out_viagem, async () => {
    await db.out_carreteiro.put(ficha)
    // Substitui qualquer viagem antiga do mesmo carreteiroId (reenvio após correção)
    await db.out_viagem.where('carreteiroId').equals(ficha.id).delete()
    await db.out_viagem.bulkAdd(viagens)
  })

  void requestBgSync('outbox-data')

  let pushResult: PushSummary | null = null
  try {
    pushResult = await pushCarreteiroOutbox()
  } catch (e) {
    console.warn('[carreteiroRepo] push falhou; mantendo no outbox:', e)
  }

  // Trigger global pra feedback no indicador + flush de filas relacionadas
  void triggerForegroundSync('post-save-carreteiro', { ignoreCooldown: true })

  return { pushResult }
}

function draftToViagem(v: ViagemDraft, s: CarreteiroFichaState): OutViagem {
  const n = normalizarViagem(s.tipo, s.fichaConcreto, paraRegra(v, s.fichaConcreto), ctxDaViagem(v))
  return {
    id: v.id,
    carreteiroId: s.id,
    servicoExecutadoId: v.servico.id,
    servicoExecutadoNome: v.servico.nome,
    // Só prévia — o servidor resolve o item de contrato vigente na data.
    contratoItemId: v.servico.contratoItemId ?? null,
    mveId: s.mveId!,
    materialId: v.servico.materialId,
    materialNome: v.servico.materialNome,
    localCargaId: n.localCargaId,
    localCargaNome: v.localCargaNome,
    cargaChapaApontador: n.cargaChapaApontador,
    cargaHora: n.cargaHora,
    cargaEstacaOrigem: n.cargaEstacaOrigem,
    cargaDtFixo: v.cargaDtFixo,
    localDescargaId: n.localDescargaId,
    localDescargaNome: v.localDescargaNome,
    descargaChapaApontador: n.descargaChapaApontador,
    descargaHora: n.descargaHora,
    descargaEstacaDescarga: n.descargaEstacaDescarga,
    descargaBairro: n.descargaBairro,
    descargaRua: n.descargaRua,
    descargaLargura: n.descargaLargura,
    descargaComprimento: n.descargaComprimento,
    descargaPosicao: n.descargaPosicao,
    qdeVg: 1,
    // Prévia (auditoria offline). O servidor recalcula com o contrato vigente.
    producao: viagemProducao(s, v),
    concreto: s.fichaConcreto,
    nf: n.nf,
    volume: v.volume,
    deletado: 'N',
  }
}

/**
 * Ficha recusada pelo servidor → volta a ser rascunho, com a recusa anexada
 * para destacar o campo. Sai do outbox (salvar de novo recria como PENDING).
 * null se a ficha não guardou o estado (outbox de versão antiga).
 */
export async function reabrirFichaRecusada(id: string): Promise<CarreteiroFichaState | null> {
  const row = await db.out_carreteiro.get(id)
  if (!row?.draftState) return null
  let state: CarreteiroFichaState
  try {
    state = { ...migrarEstado(JSON.parse(row.draftState)), rejeicao: row.rejeicao ?? null }
  } catch {
    return null
  }
  await db.transaction('rw', db.out_carreteiro, db.out_viagem, db.draft_carreteiro, async () => {
    await upsertDraft(state)
    await db.out_viagem.where('carreteiroId').equals(id).delete()
    await db.out_carreteiro.delete(id)
  })
  return state
}

/** Descarta de vez uma ficha que está no outbox (usado nas recusadas). */
export async function descartarFichaOutbox(id: string): Promise<void> {
  await db.transaction('rw', db.out_carreteiro, db.out_viagem, async () => {
    await db.out_viagem.where('carreteiroId').equals(id).delete()
    await db.out_carreteiro.delete(id)
  })
}
